/**
 * Ownership-based state for the B2B Create Order AI auto-fill.
 *
 * The problem this solves: "fill blank fields only" protects what the user
 * typed, but it also protects the PREVIOUS document's guesses. Extract from
 * invoice A, then from invoice B, and A's description survives — producing a
 * form that mixes two shipments.
 *
 * The rule here is ownership rather than blankness:
 *
 *   The AI still OWNS a field if its current value equals what the AI wrote.
 *   If they differ, the user edited it, and the value is theirs.
 *
 * Ownership is derived by comparison, never stored as a flag, so it cannot
 * drift out of sync with the form.
 *
 *   - writable on a new extraction  = blank OR AI-owned
 *   - revertable on remove/replace  = AI-owned only
 *
 * `previous` is captured on the FIRST AI touch of a field and carried forward,
 * so reverting restores what the user had before the AI ever ran — not an
 * earlier AI guess.
 *
 * Arrays (reference numbers, invoices, boxes) use coarse ownership: the AI owns
 * the whole array only while it still matches what the AI wrote. Touch any row
 * and the array becomes the user's, falling back to the original fill-blank
 * merge. That errs toward preserving the user's work.
 *
 * Pure functions — no React, no DOM — so the pages stay thin and this is
 * directly testable.
 */

// Scalar fields on formData the AI may fill, with their UI labels.
export const SCALAR_FIELDS = [
  ["description", "Description"],
  ["po_expiry_date", "PO expiry date"],
  ["b2b_marketplace_id", "Marketplace"],
  ["appointment_date", "Appointment date"],
  ["appointment_time_range", "Appointment slot"],
  ["eway_bill_number", "E-way bill number"],
  // Set from the consignee block, via matchDropLocation below. Owned like
  // any other AI value: reverted when the document changes, and never
  // overwritten once the user picks a location themselves.
  ["drop_location_id", "Drop location"],
];

// These always hold a value, so "blank" cannot be the writability test. They
// are writable while still at their untouched default (existing behaviour),
// or while AI-owned (the new part).
export const DEFAULTED_FIELDS = [
  ["payment_mode", "Prepaid", "Payment mode"],
  ["freight_mode", "BTC", "Freight mode"],
];

export const ARRAY_FIELDS = [
  ["referenceNumbers", "reference_numbers", "Reference numbers"],
  ["invoices", "invoices", "Invoices"],
  ["boxes", "boxes", "Boxes"],
];

const isBlank = (v) => v === undefined || v === null || v === "";

const same = (a, b) => String(a ?? "") === String(b ?? "");

/** Structural comparison; row order and keys both matter. */
const sameRows = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i] || {};
    const keys = new Set([...Object.keys(row || {}), ...Object.keys(other)]);
    return [...keys].every((k) => same(row?.[k], other[k]));
  });
};

/**
 * Rows stored in the ownership record must never share objects with the rows
 * handed to React. The pages edit a row with `updated[index][field] = value`,
 * which mutates the object in place - so a shared reference would quietly
 * rewrite the record along with the form, and the AI would keep believing it
 * owned a row the user had just changed.
 */
const cloneRows = (rows) => (rows || []).map((r) => ({ ...r }));

const isRowBlank = {
  referenceNumbers: (r) => !r.reference_number,
  invoices: (r) => !r.invoice_number && !r.invoice_amount,
  boxes: (r) => !r.length && !r.width && !r.height && !r.weight,
};

/** The original fill-blank merge, used when the array is NOT AI-owned. */
const mergeRows = (current, incoming, blankTest) => {
  const rows = current.map((r) => ({ ...r }));
  incoming.forEach((row) => {
    const slot = rows.findIndex(blankTest);
    if (slot !== -1) rows[slot] = { ...rows[slot], ...row };
    else rows.push({ ...row });
  });
  return rows;
};

// Legal-form and filler words carry no identity: "Blink Commerce Pvt Ltd" and
// "BLINK COMMERCE PRIVATE LIMITED" are the same company.
const LEGAL_TOKENS = new Set([
  "pvt", "private", "ltd", "limited", "llp", "inc", "incorporated",
  "corp", "corporation", "co", "company", "and", "the",
]);

export const normalizeCompanyName = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((t) => t && !LEGAL_TOKENS.has(t))
    .join(" ");

/** The shorter name must be a whole-token prefix of the longer one, so a
 *  document's "Blink Commerce Hyderabad" still matches a saved "Blink Commerce"
 *  while "Commerce Traders" never matches "Blink Commerce". */
const tokenPrefixMatch = (a, b) => {
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length > 0 && short.every((token, i) => token === long[i]);
};

/**
 * Find the seller's saved drop location that the document is shipping to.
 *
 * Deliberately conservative. A misdelivered freight consignment costs far more
 * than an unfilled dropdown, so this returns a location only when the PIN code
 * matches exactly, the company name agrees, and EXACTLY ONE saved location
 * fits. Two plausible candidates mean no selection.
 *
 * It never invents or creates a location — it only points at one the seller
 * already has.
 */
export function matchDropLocation(locations, data) {
  const pincode = String(data?.consignee_pincode ?? "").trim();
  if (!/^[1-9][0-9]{5}$/.test(pincode)) return null;

  const name = normalizeCompanyName(data?.consignee_name);
  // Too little left to identify anyone once the legal words are gone.
  if (name.replace(/ /g, "").length < 4) return null;
  const nameTokens = name.split(" ");

  const fits = (locations || []).filter((loc) => {
    if (String(loc?.pincode ?? "").trim() !== pincode) return false;
    const locTokens = normalizeCompanyName(loc?.company_name).split(" ").filter(Boolean);
    return tokenPrefixMatch(nameTokens, locTokens);
  });

  return fits.length === 1 ? fits[0] : null;
}

export const emptyOwnership = () => ({ scalars: {}, arrays: {} });

/**
 * Is this scalar still owned by the recorded AI extraction?
 * Unknown field, or a value the user has since changed, means no.
 */
export const ownsScalar = (owned, key, currentValue) => {
  const rec = owned?.scalars?.[key];
  return !!rec && same(currentValue, rec.wrote);
};

export const ownsArray = (owned, key, currentRows) => {
  const rec = owned?.arrays?.[key];
  return !!rec && sameRows(currentRows, rec.wrote);
};

/**
 * Apply an extraction result over the current form state.
 *
 * @param state  { formData, referenceNumbers, invoices, boxes }
 * @param data   normalized extraction payload from the backend
 * @param owned  ownership record from the PREVIOUS extraction (or null)
 * @returns { formData, referenceNumbers, invoices, boxes, owned, filled, skipped, replaced }
 */
export function applyExtraction(state, data, owned, options = {}) {
  // The document names a consignee, not an EdgeLMS id. Resolve it against
  // the seller's saved locations before the normal ownership rules run, so
  // the result is protected and reverted exactly like every other AI value.
  const matchedDropLocation = matchDropLocation(options.dropLocations, data);
  const resolved = matchedDropLocation
    ? { ...data, drop_location_id: matchedDropLocation.id }
    : data;

  const formData = { ...state.formData };
  const nextOwned = emptyOwnership();
  const filled = [];
  const skipped = [];
  const replaced = [];

  const remember = (key, previous, wrote) => {
    // Carry the ORIGINAL pre-AI value forward across repeated extractions, so a
    // later revert restores the user's value rather than an earlier AI guess.
    const prior = owned?.scalars?.[key];
    nextOwned.scalars[key] = {
      wrote,
      previous: prior ? prior.previous : previous,
    };
  };

  for (const [key, label] of SCALAR_FIELDS) {
    const incoming = resolved?.[key];
    const current = formData[key];
    if (isBlank(incoming)) {
      // Nothing offered. Keep any existing ownership so a later revert still
      // knows this field came from the AI.
      if (owned?.scalars?.[key] && ownsScalar(owned, key, current)) {
        nextOwned.scalars[key] = owned.scalars[key];
      }
      continue;
    }
    if (isBlank(current)) {
      formData[key] = incoming;
      remember(key, current, incoming);
      filled.push(label);
    } else if (ownsScalar(owned, key, current)) {
      if (!same(current, incoming)) {
        formData[key] = incoming;
        replaced.push(label);
      }
      remember(key, current, incoming);
    } else if (!same(current, incoming)) {
      skipped.push(label);           // the user's value — never touched
    }
  }

  for (const [key, defaultValue, label] of DEFAULTED_FIELDS) {
    const incoming = resolved?.[key];
    const current = formData[key];
    if (isBlank(incoming)) {
      if (owned?.scalars?.[key] && ownsScalar(owned, key, current)) {
        nextOwned.scalars[key] = owned.scalars[key];
      }
      continue;
    }
    const atDefault = same(current, defaultValue);
    if (ownsScalar(owned, key, current)) {
      if (!same(current, incoming)) {
        formData[key] = incoming;
        replaced.push(label);
      }
      remember(key, current, incoming);
    } else if (atDefault) {
      if (!same(incoming, defaultValue)) {
        formData[key] = incoming;
        remember(key, current, incoming);
        filled.push(label);
      }
    } else if (!same(current, incoming)) {
      skipped.push(label);
    }
  }

  const out = { formData };

  for (const [stateKey, dataKey, label] of ARRAY_FIELDS) {
    const current = state[stateKey] || [];
    const incoming = resolved?.[dataKey];

    if (!incoming || incoming.length === 0) {
      out[stateKey] = current;
      if (owned?.arrays?.[stateKey] && ownsArray(owned, stateKey, current)) {
        nextOwned.arrays[stateKey] = owned.arrays[stateKey];
      }
      continue;
    }

    if (ownsArray(owned, stateKey, current)) {
      // Still the AI's rows — this document replaces them wholesale.
      const rows = cloneRows(incoming);
      out[stateKey] = rows;
      const prior = owned.arrays[stateKey];
      nextOwned.arrays[stateKey] = {
        wrote: cloneRows(rows),
        previous: cloneRows(prior.previous),
      };
      replaced.push(`${label} (${rows.length})`);
    } else if (owned?.arrays?.[stateKey]) {
      // The user edited these rows after the last extraction. Leave them be.
      out[stateKey] = current;
      skipped.push(label);
    } else {
      // No prior AI ownership: original fill-blank behaviour.
      const rows = mergeRows(current, incoming, isRowBlank[stateKey]);
      out[stateKey] = rows;
      nextOwned.arrays[stateKey] = {
        wrote: cloneRows(rows),
        previous: cloneRows(current),
      };
      filled.push(`${label} (${incoming.length})`);
    }
  }

  return {
    ...out, owned: nextOwned, filled, skipped, replaced, matchedDropLocation,
  };
}

/**
 * Undo an extraction: restore values still owned by it, leave the user's alone.
 *
 * Used when the AI document is removed, and as the first half of a replace.
 *
 * @returns { formData, referenceNumbers, invoices, boxes, owned, reverted, kept }
 */
export function revertExtraction(state, owned) {
  const formData = { ...state.formData };
  const reverted = [];
  const kept = [];

  const labelOf = (key) =>
    (SCALAR_FIELDS.find(([k]) => k === key) || DEFAULTED_FIELDS.find(([k]) => k === key) || [])
      .slice(-1)[0] || key;

  for (const [key, rec] of Object.entries(owned?.scalars || {})) {
    if (same(formData[key], rec.wrote)) {
      formData[key] = rec.previous;
      reverted.push(labelOf(key));
    } else {
      kept.push(labelOf(key));       // user edited it after extraction
    }
  }

  const out = { formData };
  for (const [stateKey, , label] of ARRAY_FIELDS) {
    const current = state[stateKey] || [];
    const rec = owned?.arrays?.[stateKey];
    if (rec && sameRows(current, rec.wrote)) {
      out[stateKey] = cloneRows(rec.previous);
      reverted.push(label);
    } else {
      out[stateKey] = current;
      if (rec) kept.push(label);
    }
  }

  return { ...out, owned: emptyOwnership(), reverted, kept };
}
